import {
  BooleanField,
  DateTimeField,
  NumberField,
  RelationshipField,
  RelationshipFieldMany,
  StringField,
  LongTextField,
} from "../plugins/Cruxtify/field-types";
import {
  BooleanProperty,
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  RelationshipPropertyMany,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import { ApcCrudModel } from "./ApcCrudModel";

export class Product extends ApcCrudModel {
  public static icon = "mdi-package-variant-closed";
  protected static _typeLabel = "Product";
  protected static asProperty = "product";
  public static api = {
    path: "products",
  };

  protected static routeBase = "products";

  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.LoggedIn]: UserPermission.Read,
    },
    UserPermission.New
  );

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "deleted At",
        userPermissions: UserPermission.Hidden,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "name",
        label: "Product Name",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "part_number",
        label: "Part Number",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "description",
        label: "Description",
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_baba_compliant",
        label: "Is BABA Compliant",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "product_line",
        label: "Product Line",
        relatedModel: "ProductLine",
        foreignProperty: "products",
        maxItems: 1,
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "configurable_slots",
        label: "Configurable Slots",
        relatedModel: "ConfigurableSlot",
        foreignProperty: "product",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "attribute_options",
        label: "Attributes",
        relatedModel: "AttributeOption",
        foreignProperty: "products",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "configurations",
        label: "Configurations",
        relatedModel: "Configuration",
        foreignProperty: "product",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    { type: DateTimeField, opts: { property: "created_at" } },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: StringField, opts: { property: "name", isPrimaryLabelField: 1 } },
    { type: StringField, opts: { property: "part_number" } },
    { type: LongTextField, opts: { property: "description" } },
    { type: BooleanField, opts: { property: "is_baba_compliant" } },
    {
      type: RelationshipField,
      opts: {
        property: "product_line",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "configurable_slots",
        viewComponentName: "view-relationship-field-many-labels",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutId: "field",
        requiresDetachedLabel: true,
        cols: 12,
        collectionLayoutOpts: {
          cols: 12,
        },
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "configurations",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "attribute_options",
        viewComponentName: "view-product-attribute-options",
        formComponentName: "form-product-attribute-options",
        cols: 12,
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: [
        "name",
        "part_number",
        "product_line",
        "description",
        "created_at",
        "updated_at",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      useModal: false,
      dynamicFilterOptions: {
        filterFields: [
          "name",
          "part_number",
          "product_line",
          "description",
          "is_baba_compliant",
          "created_at",
          "updated_at",
        ],
      },
      defaultSort: [
        {
          field: "name",
          order: "asc",
        },
      ],
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      type: "TabbedModelLayout",
      opts: {
        id: "tabs",
        tabs: [
          {
            id: "detailsTab",
            label: "Details",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "productDetails",
                fieldSets: [
                  {
                    id: "main",
                    fields: [
                      "name",
                      "part_number",
                      "product_line",
                      "description",
                      "is_baba_compliant",
                    ],
                  },
                  {
                    id: "attributes",
                    fields: ["attribute_options"],
                  },
                  {
                    id: "slots",
                    fields: ["configurable_slots"],
                  },
                  {
                    id: "meta",
                    fields: ["created_at", "updated_at", "id"],
                  },
                ],
              },
            },
          },
          {
            id: "configurationsTab",
            label: "Configurations",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                fieldSets: [
                  {
                    id: "configurations",
                    fields: ["configurations"],
                  },
                ],
              },
            },
          },
        ],
      },
    },
  ];
}
